import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import {
  ThreadId,
  EnvironmentId,
  type CmmdProjectTaskDraft,
  type CmmdProjectTaskDraftProposal,
  type ScopedProjectRef,
  type ScopedThreadRef,
} from "@cmmd-forge/contracts";
import type {
  EnsureProjectLeadTaskCreationThreadInput,
  EnsureProjectLeadTaskCreationThreadResult,
} from "~/lib/projectLeadTaskCreationThread";

import { CmmdApiError } from "~/environments/cmmdApi";

/** Shared shape of `ProjectLeadTaskDraftController["start"]`'s single argument —
 * hoisted here (rather than derived with `Parameters<...>` from the controller
 * interface in the hook file) so this module never imports back from the hook
 * it feeds, which would be a cycle. */
export interface ProjectLeadTaskDraftStartInput {
  readonly title: string;
  readonly description: string;
  readonly priority: CmmdProjectTaskDraftProposal["tasks"][number]["priority"];
  readonly workItemType: CmmdProjectTaskDraftProposal["tasks"][number]["workItemType"];
}

export function randomKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim().length > 0 ? error.message : fallback;
}

export function isDefinitiveCreateFailure(cause: unknown): boolean {
  const status = cause instanceof CmmdApiError ? cause.status : null;
  return (
    status !== null &&
    status >= 400 &&
    status < 500 &&
    status !== 401 &&
    status !== 408 &&
    status !== 429
  );
}

export function isCreateIdempotencyConflict(cause: unknown): boolean {
  const idempotencyConflictMessage =
    "Idempotency key was already used for a different task draft request";
  if (!(cause instanceof Error)) return false;
  const status = "status" in cause && typeof cause.status === "number" ? cause.status : null;
  const code = "code" in cause && typeof cause.code === "string" ? cause.code : null;
  return (
    status === 409 &&
    (code === "TASK_DRAFT_IDEMPOTENCY_CONFLICT" || cause.message === idempotencyConflictMessage)
  );
}

/** Shared by useProjectLeadTaskDraft.attempts.logic.ts too — both the active
 * draft pointer and the pending create attempt live in localStorage under the
 * same owner/project pair, so the two key builders stay next to each other. */
export const activeDraftStorageKey = (ownerId: string, projectId: number): string =>
  `forge:project-lead-task-draft:${ownerId}:${projectId}`;
export const pendingCreateStorageKey = (ownerId: string, projectId: number): string =>
  `forge:project-lead-task-draft-create:${ownerId}:${projectId}`;

export interface StoredDraftPointer {
  readonly draftId: number;
  readonly environmentId: string | null;
}

function parseStoredDraftPointer(value: string | null): StoredDraftPointer | null {
  const legacyId = Number(value);
  if (Number.isInteger(legacyId) && legacyId > 0) return { draftId: legacyId, environmentId: null };
  try {
    const parsed = JSON.parse(value ?? "null") as Partial<StoredDraftPointer> | null;
    if (parsed === null || !Number.isInteger(parsed.draftId) || parsed.draftId! <= 0) return null;
    return {
      draftId: parsed.draftId!,
      environmentId: typeof parsed.environmentId === "string" ? parsed.environmentId : null,
    };
  } catch {
    return null;
  }
}

export function storedDraftPointer(
  ownerId: string | null,
  projectId: number,
): StoredDraftPointer | null {
  if (ownerId === null) return null;
  let value: string | null = null;
  try {
    value = window.localStorage.getItem(activeDraftStorageKey(ownerId, projectId));
  } catch {
    return null;
  }
  return parseStoredDraftPointer(value);
}

export function removeStoredDraft(
  ownerId: string,
  projectId: number,
  expectedDraftId: number,
): void {
  try {
    const key = activeDraftStorageKey(ownerId, projectId);
    const current = storedDraftPointer(ownerId, projectId);
    if (current?.draftId === expectedDraftId) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Storage may be unavailable; CMMD draft state remains authoritative.
  }
}

export function restoredThreadRef(
  projectRef: ScopedProjectRef | null,
  draft: CmmdProjectTaskDraft,
  storedEnvironmentId: string | null,
): ScopedThreadRef | null {
  const environmentId = storedEnvironmentId ?? projectRef?.environmentId ?? null;
  return environmentId === null
    ? null
    : scopeThreadRef(EnvironmentId.make(environmentId), ThreadId.make(draft.forgeThreadId));
}

export class ProjectLeadDraftThreadRestoreError extends Error {
  override readonly name = "ProjectLeadDraftThreadRestoreError";
}

export async function ensureRestoredDraftThread(input: {
  readonly draft: CmmdProjectTaskDraft;
  readonly projectId: number;
  readonly projectRef: ScopedProjectRef | null;
  readonly storedEnvironmentId: string | null;
  readonly ensureThread: (
    input: EnsureProjectLeadTaskCreationThreadInput,
  ) => Promise<EnsureProjectLeadTaskCreationThreadResult>;
}): Promise<ScopedThreadRef> {
  const threadRef = restoredThreadRef(input.projectRef, input.draft, input.storedEnvironmentId);
  if (threadRef === null || input.projectRef === null) {
    throw new Error("Connect this CMMD project to a Forge project before restoring Project Lead.");
  }
  try {
    const ensured = await input.ensureThread({
      projectRef: { ...input.projectRef, environmentId: threadRef.environmentId },
      cmmdProjectId: String(input.projectId),
      taskDraftId: String(input.draft.id),
      contextPrompt: `Review and improve task draft ${input.draft.id}: ${input.draft.proposal?.goal ?? "Task draft"}`,
      requiredThreadId: threadRef.threadId,
    });
    return ensured.threadRef;
  } catch (cause: unknown) {
    throw new ProjectLeadDraftThreadRestoreError(
      "The Project Lead conversation was not restored.",
      {
        cause,
      },
    );
  }
}

export function isCurrentRestore(
  cancelled: boolean,
  generationRef: { readonly current: number },
  generation: number,
): boolean {
  return !cancelled && generationRef.current === generation;
}
